import moment from 'moment-timezone';

import {
  ACCOMMODATION_FORMS_NUMBERS,
  ACCOMMODATION_FORMS_ORDER
} from 'pages/create-accommodation/constants';

import {
  getFuturePrices,
  getAddOns,
  getMandatoryFees,
  getIntegrations,
  getPreparedPricesCurrency
} from 'utils/accommodations-and-activities/prepareAccommodationAndActivityData';

export const getPreparedDataToCreateAccommodation = (
  forms,
  pricesActiveSubTab,
  languages,
  isUpdate,
  deletedAddOns,
  deletedMandatoryFees,
  deletedRelatedActivities,
  deletedIntegrations,
  isCopy,
) => {
  const languagesLength = languages.length;
  let secondLanguage = false;
  if (languagesLength > 1) secondLanguage = languages[languagesLength - 1];

  const generalForm = _getGeneralForm(forms);
  const descriptionForm = { ...forms['descriptionForm'].values };
  const integrationForm = { ...forms['integrationForm'].values };
  const addOnsForm = { ...forms['addOnsForm'].values };
  const mandatoryFeesForm = { ...forms['mandatoryFeesForm'].values };
  const futurePricesForm = { ...forms['futurePricesForm'].values };
  const relatedForm = _getRelatedForm(forms);

  let futurePrices = getFuturePrices(isUpdate, futurePricesForm);
  let addOns = getAddOns(isUpdate, addOnsForm, secondLanguage);
  let mandatoryFees = getMandatoryFees(isUpdate, mandatoryFeesForm, secondLanguage);
  let integrations = getIntegrations(isUpdate, integrationForm);
  const ownerIdObj = {
    businessOwnerId:
      forms.businessOwner.selectedBusinessOwner.ownerId ||
      forms.businessOwner.selectedBusinessOwner.id
  };

  const pricesFormName =
    ACCOMMODATION_FORMS_ORDER[ACCOMMODATION_FORMS_NUMBERS.pricesForm][
      pricesActiveSubTab
    ];

  const pricesForm = forms[pricesFormName]?.values || {};
  const prices = _getPrices(pricesForm);
  const photoObj = forms['generalForm'].values.coverPhoto.file
    ? { photo: forms['generalForm'].values.coverPhoto.file }
    : {};

  delete generalForm.coverPhoto;
  delete generalForm.activityType;

  let deletedAddOnsObj = deletedAddOns.length
    ? {
        deletedAddOns: deletedAddOns
      }
    : {};
  
  let deletedMandatoryFeesObj = deletedMandatoryFees.length
  ? {
    deletedMandatoryFees: deletedMandatoryFees
    }
  : {};

  let deletedRelatedActivitiesObj = deletedRelatedActivities.length
    ? { deletedRelatedActivities: deletedRelatedActivities }
    : {};

  let deletedPlatformsObj = deletedIntegrations.length
    ? { deletedPlatforms: deletedIntegrations }
    : {};

  const deactivationPeriods = JSON.parse(
    JSON.stringify(generalForm.deactivationPeriods)
  ).filter(({ startPeriod, endPeriod }) => !!startPeriod && !!endPeriod);

  delete generalForm.deactivationPeriods;

  if (isCopy) {
    delete generalForm.id;
    generalForm.productCode = '';
    addOns = addOns.map(addOn => ({ ...addOn, id: null }));
    mandatoryFees = mandatoryFees.map(mandatoryFee => ({ ...mandatoryFee, id: null }));
    integrations = integrations.map(integration => ({
      ...integration,
      id: null
    }));
    deletedAddOnsObj = {};
    deletedMandatoryFeesObj = {};
    deletedRelatedActivitiesObj = {};
    deletedPlatformsObj = {};
  }

  return {
    ...photoObj,
    ...ownerIdObj,
    accommodation: {
      ...generalForm,
      ..._getGeneralInfoSecondLanguage(forms, secondLanguage),
      ...descriptionForm,
      ..._getDescriptionInfoSecondLanguage(forms, secondLanguage),
      ...relatedForm,
      prices: prices,
      futureRatePlanIds: futurePrices,
      accommodationOnPlatform: integrations
    },
    addOns: addOns,
    ...deletedAddOnsObj,
    mandatoryFees: mandatoryFees,
    ...deletedMandatoryFeesObj,
    ...deletedRelatedActivitiesObj,
    ...deletedPlatformsObj,
    deactivationPeriods
  };
};

const _getGeneralForm = forms => {
  const generalFormValues = { ...forms['generalForm'].values };
  const idObj = generalFormValues.id ? { id: generalFormValues.id } : {};

  return {
    ...idObj,
    title: generalFormValues.title,
    productCode: generalFormValues.productCode,
    guestsNumber: parseInt(generalFormValues.guests),
    location: generalFormValues.location,
    fromDate: moment(generalFormValues.availability.startDate).format(
      'YYYY-MM-DD'
    ),
    toDate: generalFormValues.availability.endDate
      ? moment(generalFormValues.availability.endDate).format('YYYY-MM-DD')
      : null,
    checkInTime: moment(generalFormValues.checkInAfter).format('HH:mm:ssZZ'),
    checkOutTime: moment(generalFormValues.checkOutBefore).format('HH:mm:ssZZ'),
    bedDetails: generalFormValues.bedDetails,
    minNights: generalFormValues.minNights,
    color:generalFormValues.color,
    deactivationPeriods: generalFormValues.deactivatedDates.map(
      ({ startDate, endDate, id, isCanceled }) => ({
        id,
        startPeriod: startDate?.format('YYYY-MM-DD'),
        endPeriod: endDate?.format('YYYY-MM-DD'),
        isCanceled
      })
    )
  };
};

const _getGeneralInfoSecondLanguage = (forms, secondLanguage) => {
  const countryCode = 'No';

  if (!secondLanguage)
    return {
      [`title${countryCode}`]: null,
      [`bedDetails${countryCode}`]: null
    };

  const generalFormSecondLanguage = {
    ...forms[`generalForm-${secondLanguage}`].values
  };

  return {
    [`title${countryCode}`]: generalFormSecondLanguage.title,
    [`bedDetails${countryCode}`]: generalFormSecondLanguage.bedDetails
  };
};

const _getDescriptionInfoSecondLanguage = (forms, secondLanguage) => {
  const countryCode = 'No';

  if (!secondLanguage) {
    return {
      [`overview${countryCode}`]: null,
      [`additionalInfo${countryCode}`]: null,
      [`included${countryCode}`]: [],
      [`notIncluded${countryCode}`]: []
    };
  }

  const generalFormSecondLanguage = {
    ...forms[`descriptionForm-${secondLanguage}`].values
  };
  const newSecondLanguageObject = {};

  Object.keys(generalFormSecondLanguage).map(
    field =>
      (newSecondLanguageObject[`${field}${countryCode}`] =
        generalFormSecondLanguage[field])
  );

  return newSecondLanguageObject;
};

const _getRelatedForm = forms => {
  return {
    relatedActivities: forms[
      'relatedActivitiesForm'
    ].values.relatedActivities.map(item => parseInt(item.id))
  };
};

const _getPricesPerDay = days => {
  const prices = [];

  days.forEach(day => {
    getPreparedPricesCurrency(day).forEach(price => {
      prices.push({
        ...price,
        day: day.day
      });
    });
  });

  return prices;
};

const _getPrices = pricesForm => {
  if (pricesForm.categories) {
    let categoryPrices = [];
    pricesForm.categories.map(item => {
      categoryPrices = [...categoryPrices, ...getPreparedPricesCurrency(item)];
    });
    return categoryPrices;
  } else if (pricesForm.days) {
    return _getPricesPerDay(pricesForm.days);
  } else {
    return getPreparedPricesCurrency(pricesForm);
  }
};
